import { useEffect, useRef, useState } from 'react';
import * as dashboardApi from '@/features/dashboard/api/dashboard-api';
import { useEditorStore } from '../state/editor-store';
import { normalizeEditorGraph } from '../domain/normalize-graph';
import { AutosaveCoordinator } from './autosave-coordinator';
import { serializeEditorGraph } from './serializer';
import { createRecoveryStore } from './recovery/indexeddb-recovery-store';
import type { RecoveryRecord, RecoveryStore } from './recovery/recovery-store';
import type { SaveState } from './save-state';

const RECOVERY_WRITE_DEBOUNCE_MS = 700;

export interface UseAutosaveOptions {
  funnelId: string;
  userId: string;
  initialRevision: number;
  schemaVersion: number;
  recoveryStore?: RecoveryStore;
}

export interface UseAutosaveResult {
  state: SaveState;
  revision: number;
  errorMessage: string | undefined;
  recoveryAvailable: boolean;
  retry: () => void;
  flush: () => Promise<boolean>;
  reloadServerVersion: () => Promise<void>;
  dismissConflictKeepLocal: () => void;
}

/**
 * SPEC-007 — the single hook wiring the store, the AutosaveCoordinator and
 * the recovery store together for one editor session. Nothing else in the
 * component tree schedules a timer or talks to the save endpoint directly
 * (plan §31 "Não espalhar timers em componentes").
 */
export function useAutosave(options: UseAutosaveOptions): UseAutosaveResult {
  const { funnelId, userId, initialRevision, schemaVersion } = options;
  const [state, setState] = useState<SaveState>('clean');
  const [errorMessage, setErrorMessage] = useState<string | undefined>(undefined);
  const [recoveryAvailable, setRecoveryAvailable] = useState(false);
  const [revision, setRevision] = useState(initialRevision);

  const recoveryStoreRef = useRef<RecoveryStore>(options.recoveryStore ?? createRecoveryStore().store);
  // Shared with the mutation-subscription effect below so a save that
  // completes before the debounced recovery write fires can cancel it —
  // otherwise the write (scheduled from the *same* mutation the save just
  // confirmed) can land after `remove()` and leave a stale, misleading
  // recovery record behind for state that is already safely persisted.
  const recoveryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const coordinatorRef = useRef<AutosaveCoordinator | null>(null);
  if (!coordinatorRef.current) {
    coordinatorRef.current = new AutosaveCoordinator({
      initialRevision,
      getGraphSnapshot: () => serializeEditorGraph(useEditorStore.getState().graph!),
      save: (payload, signal) => dashboardApi.saveFunnelGraph(funnelId, payload.expectedRevision, payload.graph, signal),
      onStateChange: (nextState, info) => {
        setState(nextState);
        setErrorMessage(info.message);
      },
      onSaveSuccess: ({ graph, revision: savedRevision }) => {
        setRevision(savedRevision);
        useEditorStore
          .getState()
          .markGraphPersisted((graph.nodes as { id: string }[]).map((n) => n.id));
        if (recoveryTimerRef.current) {
          clearTimeout(recoveryTimerRef.current);
          recoveryTimerRef.current = null;
        }
        void recoveryStoreRef.current
          .remove(userId, funnelId)
          .then(() => setRecoveryAvailable(false));
      },
    });
  }

  useEffect(() => {
    const coordinator = coordinatorRef.current!;
    let lastMutationVersion = useEditorStore.getState().mutationVersion;
    let lastViewportVersion = useEditorStore.getState().viewportVersion;

    const scheduleRecoveryWrite = () => {
      if (recoveryTimerRef.current) clearTimeout(recoveryTimerRef.current);
      recoveryTimerRef.current = setTimeout(() => {
        recoveryTimerRef.current = null;
        // The save that covers this exact mutation may have completed in
        // the meantime (it shares the same debounce-adjacent timing) — in
        // that case there is nothing unconfirmed left to protect.
        if (coordinator.getState() === 'clean' || coordinator.getState() === 'saved') return;
        const graph = useEditorStore.getState().graph;
        if (!graph) return;
        const record: RecoveryRecord = {
          userId,
          funnelId,
          baseRevision: coordinator.getRevision(),
          schemaVersion,
          graph: serializeEditorGraph(graph),
          savedLocallyAt: new Date().toISOString(),
        };
        void recoveryStoreRef.current.put(record).then(() => setRecoveryAvailable(true));
      }, RECOVERY_WRITE_DEBOUNCE_MS);
    };

    const unsubscribe = useEditorStore.subscribe((current) => {
      if (current.mutationVersion !== lastMutationVersion) {
        lastMutationVersion = current.mutationVersion;
        coordinator.notifyGraphMutation();
        scheduleRecoveryWrite();
      } else if (current.viewportVersion !== lastViewportVersion) {
        lastViewportVersion = current.viewportVersion;
        coordinator.notifyViewportMutation();
      }
    });

    const handleOnline = () => coordinator.goOnline();
    const handleOffline = () => coordinator.goOffline();
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      unsubscribe();
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      if (recoveryTimerRef.current) clearTimeout(recoveryTimerRef.current);
      coordinator.dispose();
    };
  }, [funnelId, userId, schemaVersion]);

  return {
    state,
    revision,
    errorMessage,
    recoveryAvailable,
    retry: () => coordinatorRef.current!.retry(),
    flush: () => coordinatorRef.current!.flush(),
    reloadServerVersion: async () => {
      const { funnel } = await dashboardApi.getFunnel(funnelId);
      useEditorStore.getState().initialize(normalizeEditorGraph(funnel.graph));
      coordinatorRef.current!.acknowledgeReload(funnel.revision);
      setRevision(funnel.revision);
      await recoveryStoreRef.current.remove(userId, funnelId);
      setRecoveryAvailable(false);
    },
    dismissConflictKeepLocal: () => coordinatorRef.current!.keepLocalWithoutOverwrite(),
  };
}
